import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, CheckCircle, AlertCircle, Camera, X,
  ScanLine, ChevronRight, FileText, Clock, Box, Truck, QrCode,
  AlertTriangle, RotateCcw, Plus, Minus, CheckSquare, Barcode,
  ChevronDown, Thermometer, Scale, Hash, Calendar, Building2,
  Wifi, WifiOff
} from 'lucide-react';
import {
  WarehouseCard, WarehouseButton, WarehouseBadge,
  WarehouseProgress, WarehouseScanResult, WarehouseStatsCard
} from '../components/WarehouseUIKit';
import { useWarehouseTheme } from '../context/WarehouseThemeContext';
import {
  isNative, HapticService, OfflineStorage, NetworkService
} from '@/services/nativeServices';
import '../styles/warehouse.css';

import { receivingAPI } from '@/shared/services/api';

export const WarehouseReceiving = () => {
  const navigate = useNavigate();
  const { isDark } = useWarehouseTheme();
  const [activeTab, setActiveTab] = useState('all');
  const [scanMode, setScanMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [receivingItem, setReceivingItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const inputRef = useRef(null);

  // Fetch shipments from API
  const fetchShipments = async () => {
    setLoading(true);
    try {
      const data = await receivingAPI.getPendingASNs();
      if (Array.isArray(data)) {
        setShipments(data.map(s => ({
          ...s,
          shipment_id: s.asn_number || s.id,
          invoice_number: s.nfe_key || s.id,
          supplier: s.supplier_name || 'Unknown Supplier'
        })));
      }
    } catch (err) {
      console.error('Error fetching shipments:', err);
      toast.error('Failed to load shipments');
    }
    setLoading(false);
  };

  // Sync offline queue when back online
  const syncOfflineQueue = async () => {
    if (!isNative() || isSyncing) return;
    setIsSyncing(true);
    const queue = await OfflineStorage.getQueue();
    if (queue.length > 0) {
      let successCount = 0;
      for (const action of queue) {
        try {
          if (action.type === 'receive_item') {
            await receivingAPI.receiveItem(action.data);
            successCount++;
          }
        } catch (err) {
          console.error('Failed to sync action:', err);
        }
      }
      await OfflineStorage.clearQueue();
      if (successCount > 0) {
        toast.success(`Synced ${successCount} offline actions`);
        fetchShipments();
      }
    }
    setIsSyncing(false);
  };

  // Initialize network status and native services
  useEffect(() => {
    const initNetwork = async () => {
      if (isNative()) {
        await NetworkService.initialize();
        setIsOnline(NetworkService.getStatus());
        NetworkService.onStatusChange((connected) => {
          setIsOnline(connected);
          if (connected) {
            toast.success('Back online - syncing data...');
            syncOfflineQueue();
          } else {
            toast.warning('You are offline - changes will sync when connected');
          }
        });
      }
    };
    initNetwork();
    fetchShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredShipments = shipments.filter(shipment => {
    if (activeTab === 'all') return true;
    return shipment.status === activeTab;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'receiving': return 'info';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'COMPLETED';
      case 'receiving': return 'RECEIVING';
      case 'pending': return 'PENDING';
      default: return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  const handleStartReceiving = async (shipment) => {
    try {
      const data = await receivingAPI.startReceiving(shipment.shipment_id, 'DOCK-1');
      setSelectedShipment({ ...shipment, ...data });
      toast.success(`Started receiving ${shipment.shipment_id}`);
    } catch (err) {
      console.error('Error starting receiving:', err);
      toast.error(err.message || 'Failed to start receiving');
      // Fallback to local selection if offline
      if (!isOnline) setSelectedShipment(shipment);
    }
  };

  const handleScan = async (barcode) => {
    if (!barcode.trim()) return;

    const result = {
      barcode,
      timestamp: new Date().toISOString()
    };

    // If no shipment selected, try to find by barcode
    if (!selectedShipment) {
      const found = shipments.find(s =>
        s.shipment_id === barcode ||
        s.invoice_number === barcode ||
        s.items?.some(i => i.sku === barcode || i.gtin === barcode)
      );
      if (found) {
        handleStartReceiving(found);
        result.status = 'OK';
        result.message = `Found shipment ${found.shipment_id}`;
      } else {
        result.status = 'error';
        result.message = 'Shipment not found';
      }
    } else {
      // Find item in selected shipment
      const item = selectedShipment.items?.find(i =>
        i.sku === barcode || i.gtin === barcode
      );
      if (item) {
        setReceivingItem(item);
        setQuantity(1);
        result.status = 'OK';
        result.message = `${item.product_name || item.sku} - Ready to receive`;
      } else {
        result.status = 'error';
        result.message = 'Item not found in this shipment';
      }
    }

    setRecentScans([result, ...recentScans.slice(0, 4)]);
    setScanInput('');
  };

  const handleReceiveItem = async () => {
    if (!receivingItem || !selectedShipment) return;

    // Haptic feedback on native
    if (isNative()) {
      await HapticService.impact('medium');
    }

    const receiveData = {
      shipment_id: selectedShipment.shipment_id,
      sku: receivingItem.sku,
      quantity: quantity
    };

    // If offline, queue for later sync
    if (!isOnline && isNative()) {
      await OfflineStorage.queueAction({
        type: 'receive_item',
        data: receiveData,
        timestamp: new Date().toISOString()
      });
      toast.info(`Queued for sync: ${quantity}x ${receivingItem.sku}`);
      // Update local state optimistically
      setSelectedShipment(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i.sku === receivingItem.sku
            ? { ...i, received: (i.received || 0) + quantity }
            : i
        )
      }));
      setReceivingItem(null);
      setQuantity(1);
      return;
    }

    try {
      await receivingAPI.receiveItem(receiveData);

      // Success haptic
      if (isNative()) {
        // Success haptic
        if (isNative()) {
          await HapticService.notification('success');
        }
        toast.success(`Received ${quantity}x ${receivingItem.sku}`);
        // Update local state
        setSelectedShipment(prev => ({
          ...prev,
          items: prev.items.map(i =>
            i.sku === receivingItem.sku
              ? { ...i, received: (i.received || 0) + quantity }
              : i
          )
        }));
        setReceivingItem(null);
        setQuantity(1);

        // Add to recent scans
        setRecentScans([{
          barcode: receivingItem.sku,
          status: 'OK',
          message: `Received ${quantity} units`,
          timestamp: new Date().toISOString()
        }, ...recentScans.slice(0, 4)]);
      }
    } catch (err) {
      console.error('Error receiving item:', err);
      // Error haptic
      if (isNative()) {
        await HapticService.notification('error');
      }
      // Queue offline if network error
      if (isNative()) {
        await OfflineStorage.queueAction({
          type: 'receive_item',
          data: receiveData,
          timestamp: new Date().toISOString()
        });
        toast.warning('Saved offline - will sync when connected');
        // Update local state optimistically
        setSelectedShipment(prev => ({
          ...prev,
          items: prev.items.map(i =>
            i.sku === receivingItem.sku
              ? { ...i, received: (i.received || 0) + quantity }
              : i
          )
        }));
        setReceivingItem(null);
        setQuantity(1);
      } else {
        toast.error('Failed to receive item');
      }
    }
  };

  const totalStats = {
    pending: shipments.filter(s => s.status === 'pending').length,
    receiving: shipments.filter(s => s.status === 'receiving').length,
    completed: shipments.filter(s => s.status === 'completed').length
  };

  return (
    <div className="warehouse-app min-h-screen pb-20" data-testid="warehouse-receiving">
      {/* Header */}
      <header
        className="wh-safe-top px-4 py-4 flex items-center gap-4"
        style={{
          background: isDark ? 'var(--wh-bg-secondary)' : 'var(--wh-primary)',
          color: isDark ? 'var(--wh-text-primary)' : 'white'
        }}
      >
        <button
          onClick={() => {
            if (receivingItem) {
              setReceivingItem(null);
            } else if (selectedShipment) {
              setSelectedShipment(null);
            } else if (scanMode) {
              setScanMode(false);
            } else {
              navigate('/mobile');
            }
          }}
          className="p-2 -ml-2 rounded-full"
          style={{ background: 'rgba(255,255,255,0.1)' }}
          data-testid="receiving-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">
            {selectedShipment ? selectedShipment.shipment_id : 'Receiving'}
          </h1>
          <p className="text-sm opacity-80">
            {selectedShipment ? selectedShipment.supplier : 'Scan inbound shipments'}
          </p>
        </div>
        {!selectedShipment && (
          <div className="flex items-center gap-2">
            {!isOnline && (
              <div className="p-2 rounded-full bg-yellow-500">
                <WifiOff className="w-4 h-4 text-white" />
              </div>
            )}
            <button
              onClick={() => setScanMode(!scanMode)}
              className="p-3 rounded-xl"
              style={{
                background: scanMode ? 'white' : 'rgba(255,255,255,0.2)',
                color: scanMode ? 'var(--wh-primary)' : 'white'
              }}
              data-testid="scan-toggle-btn"
            >
              <ScanLine className="w-6 h-6" />
            </button>
          </div>
        )}
      </header>

      {/* Receiving Item Modal */}
      {receivingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="wh-card w-full max-w-md wh-animate-slide-up" style={{ background: 'var(--wh-bg-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: 'var(--wh-text-primary)' }}>
                Receive Item
              </h3>
              <button
                onClick={() => setReceivingItem(null)}
                className="p-2 rounded-full"
                style={{ background: 'var(--wh-bg-tertiary)' }}
              >
                <X className="w-5 h-5" style={{ color: 'var(--wh-text-secondary)' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 rounded-xl" style={{ background: 'var(--wh-bg-secondary)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--wh-primary-muted)' }}>
                  <Package className="w-6 h-6" style={{ color: 'var(--wh-primary)' }} />
                </div>
                <div className="flex-1">
                  <p className="font-bold" style={{ color: 'var(--wh-text-primary)' }}>
                    {receivingItem.product_name || receivingItem.sku}
                  </p>
                  <p className="text-sm font-mono" style={{ color: 'var(--wh-text-secondary)' }}>
                    SKU: {receivingItem.sku}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl text-center" style={{ background: 'var(--wh-warning-muted)' }}>
                  <p className="text-2xl font-bold" style={{ color: 'var(--wh-warning)' }}>
                    {receivingItem.expected || receivingItem.expected_quantity || 0}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Expected</p>
                </div>
                <div className="p-3 rounded-xl text-center" style={{ background: 'var(--wh-success-muted)' }}>
                  <p className="text-2xl font-bold" style={{ color: 'var(--wh-success)' }}>
                    {receivingItem.received || receivingItem.received_quantity || 0}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Received</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--wh-text-secondary)' }}>
                  Quantity to Receive
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: 'var(--wh-bg-tertiary)' }}
                  >
                    <Minus className="w-5 h-5" style={{ color: 'var(--wh-text-primary)' }} />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="flex-1 h-12 text-center text-2xl font-bold rounded-xl border"
                    style={{
                      background: 'var(--wh-bg-tertiary)',
                      borderColor: 'var(--wh-border)',
                      color: 'var(--wh-text-primary)'
                    }}
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: 'var(--wh-bg-tertiary)' }}
                  >
                    <Plus className="w-5 h-5" style={{ color: 'var(--wh-text-primary)' }} />
                  </button>
                </div>
              </div>

              <WarehouseButton
                variant="success"
                fullWidth
                size="large"
                icon={CheckCircle}
                onClick={handleReceiveItem}
                data-testid="confirm-receive-btn"
              >
                Confirm Receipt ({quantity} units)
              </WarehouseButton>
            </div>
          </div>
        </div>
      )}

      {/* Selected Shipment Detail View */}
      {selectedShipment && !receivingItem ? (
        <div className="px-4 mt-4 space-y-4 wh-animate-slide-up">
          {/* Shipment Info Card */}
          <WarehouseCard elevated>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--wh-text-tertiary)' }}>
                  Invoice
                </p>
                <p className="font-mono font-bold" style={{ color: 'var(--wh-text-primary)' }}>
                  {selectedShipment.invoice_number}
                </p>
              </div>
              <WarehouseBadge variant={getStatusColor(selectedShipment.status)}>
                {getStatusLabel(selectedShipment.status)}
              </WarehouseBadge>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" style={{ color: 'var(--wh-text-tertiary)' }} />
                <span className="text-sm" style={{ color: 'var(--wh-text-secondary)' }}>
                  {selectedShipment.supplier}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4" style={{ color: 'var(--wh-text-tertiary)' }} />
                <span className="text-sm" style={{ color: 'var(--wh-text-secondary)' }}>
                  {selectedShipment.carrier || 'N/A'}
                </span>
              </div>
            </div>
          </WarehouseCard>

          {/* Progress */}
          <WarehouseCard>
            <div className="flex items-center justify-between mb-2">
              <span className="wh-text-caption">Receiving Progress</span>
              <span className="font-bold" style={{ color: 'var(--wh-primary)' }}>
                {selectedShipment.items?.reduce((acc, i) => acc + (i.received || i.received_quantity || 0), 0)}/
                {selectedShipment.items?.reduce((acc, i) => acc + (i.expected || i.expected_quantity || 0), 0)} items
              </span>
            </div>
            <WarehouseProgress
              value={selectedShipment.items?.reduce((acc, i) => acc + (i.received || i.received_quantity || 0), 0)}
              max={selectedShipment.items?.reduce((acc, i) => acc + (i.expected || i.expected_quantity || 0), 0)}
              showPercentage
            />
          </WarehouseCard>

          {/* Scanner Input */}
          <WarehouseCard>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--wh-text-tertiary)' }} />
                <input
                  ref={inputRef}
                  type="text"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleScan(scanInput)}
                  placeholder="Scan item barcode..."
                  className="w-full h-12 pl-10 pr-4 rounded-xl border"
                  style={{
                    background: 'var(--wh-bg-tertiary)',
                    borderColor: 'var(--wh-border)',
                    color: 'var(--wh-text-primary)'
                  }}
                  autoFocus
                  data-testid="scan-input"
                />
              </div>
              <WarehouseButton
                variant="primary"
                onClick={() => handleScan(scanInput)}
                icon={ScanLine}
              >
                Scan
              </WarehouseButton>
            </div>
          </WarehouseCard>

          {/* Items List */}
          <div>
            <h3 className="wh-text-caption font-semibold mb-2 uppercase">Items to Receive ({selectedShipment.items?.length || 0})</h3>
            <div className="space-y-2">
              {selectedShipment.items?.map((item, i) => {
                const expected = item.expected || item.expected_quantity || 0;
                const received = item.received || item.received_quantity || 0;
                const isComplete = received >= expected;

                return (
                  <WarehouseCard
                    key={i}
                    status={isComplete ? 'success' : received > 0 ? 'info' : 'warning'}
                    interactive
                    onClick={() => {
                      setReceivingItem(item);
                      setQuantity(1);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          background: isComplete ? 'var(--wh-success-muted)' : 'var(--wh-warning-muted)'
                        }}
                      >
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5" style={{ color: 'var(--wh-success)' }} />
                        ) : (
                          <Package className="w-5 h-5" style={{ color: 'var(--wh-warning)' }} />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: 'var(--wh-text-primary)' }}>
                          {item.product_name || item.sku}
                        </p>
                        <p className="text-xs font-mono" style={{ color: 'var(--wh-text-tertiary)' }}>
                          {item.sku}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold" style={{ color: isComplete ? 'var(--wh-success)' : 'var(--wh-text-primary)' }}>
                          {received}/{expected}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--wh-text-tertiary)' }}>
                          received
                        </p>
                      </div>
                    </div>
                  </WarehouseCard>
                );
              })}
            </div>
          </div>

          {/* Recent Scans */}
          {recentScans.length > 0 && (
            <div>
              <h3 className="wh-text-caption font-semibold mb-2 uppercase">Recent Scans</h3>
              {recentScans.map((scan, i) => (
                <WarehouseScanResult key={i} {...scan} />
              ))}
            </div>
          )}
        </div>
      ) : !selectedShipment && (
        <>
          {/* Scanner Mode */}
          {scanMode && (
            <div className="px-4 mt-4 wh-animate-slide-up">
              <WarehouseCard elevated className="text-center">
                <div
                  className="wh-scanner-area mb-4"
                  style={{ borderColor: 'var(--wh-primary)' }}
                >
                  <Camera className="w-12 h-12 mb-2" style={{ color: 'var(--wh-primary)' }} />
                  <p className="wh-text-body font-medium">Point camera at barcode</p>
                  <p className="wh-text-caption">Scan shipment invoice or item barcode</p>
                </div>
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    placeholder="Enter barcode..."
                    className="flex-1 px-4 py-3 rounded-xl border"
                    style={{
                      background: 'var(--wh-bg-tertiary)',
                      borderColor: 'var(--wh-border)',
                      color: 'var(--wh-text-primary)'
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleScan(scanInput)}
                    data-testid="barcode-input"
                  />
                  <WarehouseButton
                    variant="primary"
                    onClick={() => handleScan(scanInput)}
                  >
                    Scan
                  </WarehouseButton>
                </div>
              </WarehouseCard>

              {recentScans.length > 0 && (
                <div className="mt-4">
                  <h3 className="wh-text-caption font-semibold mb-2 uppercase">Recent Scans</h3>
                  {recentScans.map((scan, i) => (
                    <WarehouseScanResult key={i} {...scan} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          {!scanMode && (
            <>
              <div
                className="px-4 py-3 flex gap-2 overflow-x-auto"
                style={{ background: 'var(--wh-bg-secondary)' }}
              >
                {[
                  { id: 'all', label: 'All' },
                  { id: 'pending', label: 'Pending' },
                  { id: 'receiving', label: 'Receiving' },
                  { id: 'completed', label: 'Completed' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
                    style={{
                      background: activeTab === tab.id ? 'var(--wh-primary)' : 'var(--wh-bg-tertiary)',
                      color: activeTab === tab.id ? 'white' : 'var(--wh-text-secondary)'
                    }}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Summary Stats */}
              <div className="px-4 mt-4">
                <div className="grid grid-cols-3 gap-3">
                  <div
                    className="text-center p-3 rounded-xl"
                    style={{ background: 'var(--wh-warning-muted)' }}
                  >
                    <p className="text-xl font-bold" style={{ color: 'var(--wh-warning)' }}>
                      {totalStats.pending}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Pending</p>
                  </div>
                  <div
                    className="text-center p-3 rounded-xl"
                    style={{ background: 'var(--wh-info-muted)' }}
                  >
                    <p className="text-xl font-bold" style={{ color: 'var(--wh-info)' }}>
                      {totalStats.receiving}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Active</p>
                  </div>
                  <div
                    className="text-center p-3 rounded-xl"
                    style={{ background: 'var(--wh-success-muted)' }}
                  >
                    <p className="text-xl font-bold" style={{ color: 'var(--wh-success)' }}>
                      {totalStats.completed}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--wh-text-secondary)' }}>Done</p>
                  </div>
                </div>
              </div>

              {/* Shipment List */}
              <div className="px-4 mt-4 space-y-3">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--wh-primary)', borderTopColor: 'transparent' }} />
                    <p className="mt-2 text-sm" style={{ color: 'var(--wh-text-secondary)' }}>Loading shipments...</p>
                  </div>
                ) : filteredShipments.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--wh-text-tertiary)' }} />
                    <p style={{ color: 'var(--wh-text-secondary)' }}>No shipments found</p>
                  </div>
                ) : (
                  filteredShipments.map(shipment => {
                    const totalExpected = shipment.items?.reduce((acc, i) => acc + (i.expected || i.expected_quantity || 0), 0) || 0;
                    const totalReceived = shipment.items?.reduce((acc, i) => acc + (i.received || i.received_quantity || 0), 0) || 0;

                    return (
                      <WarehouseCard
                        key={shipment.id}
                        interactive
                        status={getStatusColor(shipment.status)}
                        onClick={() => handleStartReceiving(shipment)}
                        data-testid={`shipment-${shipment.shipment_id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ background: 'var(--wh-info-muted)' }}
                            >
                              <FileText className="w-5 h-5" style={{ color: 'var(--wh-info)' }} />
                            </div>
                            <div>
                              <h3 className="font-semibold" style={{ color: 'var(--wh-text-primary)' }}>
                                {shipment.shipment_id}
                              </h3>
                              <p className="text-sm" style={{ color: 'var(--wh-text-secondary)' }}>
                                {shipment.supplier}
                              </p>
                            </div>
                          </div>
                          <WarehouseBadge variant={getStatusColor(shipment.status)}>
                            {getStatusLabel(shipment.status)}
                          </WarehouseBadge>
                        </div>

                        <WarehouseProgress
                          value={totalReceived}
                          max={totalExpected}
                          label={`${totalReceived}/${totalExpected} items received`}
                        />

                        <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--wh-divider)' }}>
                          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--wh-text-secondary)' }}>
                            <Clock className="w-3 h-3" />
                            <span>{shipment.expected_date ? new Date(shipment.expected_date).toLocaleDateString() : 'N/A'}</span>
                          </div>
                          {shipment.priority === 'high' && (
                            <WarehouseBadge variant="error">PRIORITY</WarehouseBadge>
                          )}
                          <ChevronRight className="w-4 h-4" style={{ color: 'var(--wh-text-tertiary)' }} />
                        </div>
                      </WarehouseCard>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* Start Scanning Button */}
          {!scanMode && (
            <div className="fixed bottom-20 left-4 right-4">
              <WarehouseButton
                variant="primary"
                fullWidth
                size="large"
                icon={ScanLine}
                onClick={() => setScanMode(true)}
                data-testid="start-scan-btn"
              >
                Start Scanning
              </WarehouseButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WarehouseReceiving;
